/*
 * Purpose: Live system health dashboard — polls public /health API
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Shield,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ContextualError } from '@/components/errors/ContextualError';
import { fetchSystemHealth } from '@/lib/systemHealthApi';
import type { HealthFetchMeta, SecurityCheck, SystemHealthSnapshot } from '@/lib/systemHealthTypes';
import { VERSION_DISPLAY } from '@/lib/appVersion';
import { cn } from '@/lib/utils';

const POLL_MS = 5000;

function statusIcon(status: SecurityCheck['status']) {
  switch (status) {
    case 'pass':
      return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
    case 'warn':
      return <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />;
    case 'fail':
      return <XCircle className="h-4 w-4 text-red-600 shrink-0" />;
    default:
      return <Activity className="h-4 w-4 text-muted-foreground shrink-0" />;
  }
}

function overallBadge(snapshot: SystemHealthSnapshot) {
  if (!snapshot.ok) {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" /> Degraded
      </Badge>
    );
  }
  if (snapshot.security.summary === 'review_recommended') {
    return (
      <Badge className="bg-amber-500/15 text-amber-800 dark:text-amber-300 border-amber-500/30 gap-1">
        <AlertTriangle className="h-3 w-3" /> Review
      </Badge>
    );
  }
  return (
    <Badge className="bg-green-500/15 text-green-800 dark:text-green-300 border-green-500/30 gap-1">
      <CheckCircle2 className="h-3 w-3" /> Operational
    </Badge>
  );
}

export function SystemHealthDashboard() {
  const [snapshot, setSnapshot] = useState<SystemHealthSnapshot | null>(null);
  const [meta, setMeta] = useState<HealthFetchMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(true);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const result = await fetchSystemHealth();
      setSnapshot(result.data);
      setMeta(result.meta);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Health fetch failed');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, tick]);

  useEffect(() => {
    if (!polling) return;
    const id = window.setInterval(() => setTick((n) => n + 1), POLL_MS);
    return () => window.clearInterval(id);
  }, [polling]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {snapshot ? overallBadge(snapshot) : null}
          <Badge variant="outline" className="gap-1 tabular-nums">
            <span className="relative flex h-2 w-2">
              {polling && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" />
              )}
              <span
                className={cn(
                  'relative inline-flex h-2 w-2 rounded-full',
                  polling ? 'bg-green-500' : 'bg-muted-foreground'
                )}
              />
            </span>
            {polling ? 'Live' : 'Paused'}
          </Badge>
          {meta && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {meta.latencyMs}ms · quota {meta.rateLimitRemaining ?? '—'}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPolling((p) => !p)}
            className="h-8"
          >
            {polling ? 'Pause' : 'Resume'} live
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void refresh()} className="h-8">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <ContextualError
          message={error}
          action={{ label: 'Retry', onClick: () => void refresh() }}
        />
      )}

      {snapshot && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="p-4">
              <p className="text-xs text-muted-foreground">API</p>
              <p className="text-lg font-semibold capitalize">{snapshot.services.api}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted-foreground">Redis</p>
              <p className="text-lg font-semibold capitalize">{snapshot.redis}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted-foreground">Uptime</p>
              <p className="text-lg font-semibold tabular-nums">{snapshot.uptimeSec}s</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted-foreground">Version</p>
              <p className="text-lg font-semibold">{snapshot.version}</p>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Security audit</CardTitle>
                <Badge variant="secondary">Grade {snapshot.security.grade}</Badge>
                <Badge variant="outline">{snapshot.security.score}%</Badge>
              </div>
              <CardDescription>
                Public posture snapshot — no secrets exposed · env: {snapshot.security.environment}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Progress value={snapshot.security.score} className="h-2" />
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="text-green-700">
                  {snapshot.security.counts.pass} pass
                </Badge>
                <Badge variant="outline" className="text-amber-700">
                  {snapshot.security.counts.warn} warn
                </Badge>
                <Badge variant="outline" className="text-red-700">
                  {snapshot.security.counts.fail} fail
                </Badge>
              </div>
              <ul className="space-y-2">
                {snapshot.security.checks.map((check) => (
                  <li
                    key={check.id}
                    className="flex gap-3 p-3 rounded-md border border-border/80 bg-muted/20 text-sm"
                  >
                    {statusIcon(check.status)}
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{check.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{check.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="border-dashed">
            <CardContent className="py-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <Clock className="h-4 w-4 shrink-0" />
              <span>
                Server time {new Date(snapshot.timestamp).toLocaleString()} · polling every{' '}
                {POLL_MS / 1000}s · UI {VERSION_DISPLAY}
              </span>
              <span className="text-border hidden sm:inline">·</span>
              <span>
                Rate limit buckets active: {snapshot.security.rateLimit.activeBuckets}
              </span>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
